/**
 * Mobile size sheet: the sticky add to cart button opens a bottom sheet listing the
 * sizes of the selected color. Picking a size selects it in the main variant picker
 * and submits the sticky add to cart form. The SizeBay "find your size" button is mirrored
 * at the bottom so its text, translation and handler come from SizeBay.
 * @see snippets/sticky-atc.liquid
 */
if (!customElements.get('size-sheet')) {
  class SizeSheet extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('[data-section-type="product-page"]')
      this.stickyBar = this.section.querySelector('.m-sticky-addtocart')
      this.stickyForm = this.stickyBar.querySelector('.sticky-atc-form')
      this.trigger = this.stickyBar.querySelector('[data-size-sheet-open]')
      this.panel = this.querySelector('.m-size-sheet__panel')
      this.list = this.querySelector('[data-size-sheet-list]')
      this.vfrSlot = this.querySelector('[data-size-sheet-vfr]')
      this.position = Number(this.dataset.optionPosition)

      this.trigger.addEventListener('click', () => this.open())
      this.querySelectorAll('[data-size-sheet-close]').forEach((el) => el.addEventListener('click', () => this.close()))
      this.addEventListener('keyup', (e) => e.code === 'Escape' && this.close())

      window.MinimogEvents.subscribe(`${this.dataset.productId}__VARIANT_CHANGE`, () => this.updateTrigger())

      this.syncGalleryHeight()
    }

    // The gallery height depends on CSS variables set after load (announcement banner, header),
    // but Swiper only measures autoHeight on init and slide change.
    // @see assets/product.css (--m-media-max-height)
    syncGalleryHeight() {
      const gallery = this.section.querySelector('.m-product-media--mobile media-gallery')
      if (!gallery || typeof ResizeObserver === 'undefined') return

      const observer = new ResizeObserver(() => gallery.slider && gallery.slider.updateAutoHeight(0))
      gallery.querySelectorAll('.m-product-media').forEach((media) => observer.observe(media))
    }

    get picker() {
      return this.section.querySelector('.m-main-product--info variant-picker')
    }

    get sizeOption() {
      return this.section.querySelector('.m-main-product--info .m-product-option--size')
    }

    // Size nodes that exist for the selected color, in the order the picker displays them
    getSizeNodes() {
      if (!this.sizeOption) return []
      return Array.from(this.sizeOption.querySelectorAll('.m-product-option--node')).filter(
        (node) => !node.classList.contains('m-product-option--node__unavailable')
      )
    }

    isSoldOut(node) {
      return node.classList.contains('m-product-option--node__soldout')
    }

    updateTrigger() {
      const available = this.getSizeNodes().some((node) => !this.isSoldOut(node))
      const text = this.trigger.querySelector('.m-add-to-cart--text')

      this.trigger.disabled = !available
      this.trigger.classList.toggle('disabled', !available)
      text.textContent = available ? window.MinimogStrings.addToCart : window.MinimogStrings.soldOut
    }

    renderSizes() {
      this.list.innerHTML = ''
      this.getSizeNodes().forEach((node) => {
        const value = node.dataset.value
        const soldOut = this.isSoldOut(node)
        const item = document.createElement('li')
        const button = document.createElement('button')

        button.type = 'button'
        button.className = 'm-size-sheet__size'
        button.disabled = soldOut
        button.innerHTML = `<span class="m-size-sheet__size-label"></span>`
        button.querySelector('.m-size-sheet__size-label').textContent = value
        if (soldOut) {
          const note = document.createElement('span')
          note.className = 'm-size-sheet__size-note'
          note.textContent = window.MinimogStrings.soldOut
          button.appendChild(note)
        }
        button.addEventListener('click', () => this.selectSize(value))

        item.appendChild(button)
        this.list.appendChild(item)
      })
    }

    selectSize(value) {
      const input = Array.from(this.sizeOption.querySelectorAll('input')).find((el) => el.value === value)
      if (!input) return

      input.checked = true
      input.dispatchEvent(new Event('change', { bubbles: true }))

      // The picker resolves the variant synchronously on change; bail out if it did not land on this size
      const variant = this.picker && this.picker.currentVariant
      if (!variant || !variant.available || variant.options[this.position - 1] !== value) return

      this.stickyForm.querySelector('[name="id"]').value = variant.id
      this.close()
      this.stickyForm.querySelector('[name="add"]').click()
    }

    // SizeBay injects its button after page load, so it is looked up each time the sheet opens
    renderVirtualFittingButton() {
      const source = this.section.querySelector('#szb-vfr-button')
      this.vfrSlot.hidden = !source
      if (!source) return

      const button = source.cloneNode(true)
      button.removeAttribute('id')
      button.removeAttribute('sizebay-product-reference')
      button.classList.add('m-size-sheet__vfr-button')

      button.addEventListener('click', () => {
        this.close()
        source.click()
      })

      this.vfrSlot.replaceChildren(button)
    }

    open() {
      this.renderSizes()
      this.renderVirtualFittingButton()
      this.classList.add('is-open')
      document.documentElement.classList.add('m:overflow-hidden')
      document.body.classList.add('m:overflow-hidden')
      this.trigger.setAttribute('aria-expanded', 'true')
      trapFocus(this.panel, this.list.querySelector('button:not([disabled])') || this.panel)
    }

    close() {
      if (!this.classList.contains('is-open')) return
      this.classList.remove('is-open')
      document.documentElement.classList.remove('m:overflow-hidden')
      document.body.classList.remove('m:overflow-hidden')
      this.trigger.setAttribute('aria-expanded', 'false')
      removeTrapFocus(this.trigger)
    }
  }

  customElements.define('size-sheet', SizeSheet)
}
